/**
 * Unified Analytics Configuration
 *
 * Centralizes all analytics platform configurations for MyArmy.
 * Used by all @akson API packages for consistent configuration management.
 */

import { z } from 'zod';
import { getOptionalEnv, getRequiredEnv } from '../config.js';
import { ECOMMERCE_EVENTS, LEAD_GENERATION_EVENTS, LEAD_SCORES } from '../events.js';

// Base configuration schema shared across all platforms
export const BaseAnalyticsConfigSchema = z.object({
	environment: z.enum(['development', 'staging', 'production']).default('production'),
	debug: z.boolean().default(false),
	retryAttempts: z.number().default(3),
	timeout: z.number().default(5000),
});

// GTM Configuration Schema
export const GTMConfigSchema = z
	.object({
		accountId: z.string(),
		containerId: z.string(),
		containerPublicId: z.string(),
		workspaceId: z.string().optional(),
		googleAdsId: z.string(),
		serviceAccount: z.object({
			email: z.string().email(),
			keyFile: z.string(),
		}),
		conversionLabels: z.object({
			// Lead Generation Conversions (existing)
			form: z.string(),
			whatsapp: z.string(),
			// E-commerce Conversions (to be created)
			purchase: z.string().optional(),
			add_to_cart: z.string().optional(),
		}),
	})
	.merge(BaseAnalyticsConfigSchema);

// Google Ads Configuration Schema
export const GoogleAdsConfigSchema = z
	.object({
		customerId: z.string(),
		managerId: z.string().optional(),
		developerToken: z.string(),
		loginCustomerId: z.string().optional(),
		serviceAccount: z.object({
			email: z.string().email(),
			keyFile: z.string(),
		}),
		conversionActions: z.object({
			// Lead Generation Actions (existing)
			whatsapp: z.string(),
			form: z.string(),
			// E-commerce Actions (to be created)
			purchase: z.string().optional(),
			add_to_cart: z.string().optional(),
		}),
		apiVersion: z.string().default('v17'),
	})
	.merge(BaseAnalyticsConfigSchema);

// PostHog Configuration Schema
export const PostHogConfigSchema = z
	.object({
		projectApiKey: z.string(),
		host: z.string().url().default('https://us.i.posthog.com'),
		personalApiKey: z.string().optional(),
		projectId: z.number().optional(),
		batchSize: z.number().default(100),
		flushAt: z.number().default(20),
		flushInterval: z.number().default(10000),
	})
	.merge(BaseAnalyticsConfigSchema);

// Meta/Facebook Configuration Schema
export const MetaConfigSchema = z
	.object({
		pixelId: z.string(),
		appId: z.string(),
		accessToken: z.string().optional(),
		testEventCode: z.string().optional(),
		apiVersion: z.string().default('v21.0'),
	})
	.merge(BaseAnalyticsConfigSchema);

// Google Search Console Configuration Schema
export const GSCConfigSchema = z
	.object({
		siteUrl: z.string().url(),
		serviceAccount: z.object({
			email: z.string().email(),
			keyFile: z.string(),
		}),
	})
	.merge(BaseAnalyticsConfigSchema);

// Combined Analytics Configuration
export const AnalyticsConfigSchema = z.object({
	gtm: GTMConfigSchema,
	googleAds: GoogleAdsConfigSchema,
	postHog: PostHogConfigSchema,
	meta: MetaConfigSchema,
	gsc: GSCConfigSchema,
});

export type AnalyticsConfig = z.infer<typeof AnalyticsConfigSchema>;
export type GTMConfig = z.infer<typeof GTMConfigSchema>;
export type GoogleAdsConfig = z.infer<typeof GoogleAdsConfigSchema>;
export type PostHogConfig = z.infer<typeof PostHogConfigSchema>;
export type MetaConfig = z.infer<typeof MetaConfigSchema>;
export type GSCConfig = z.infer<typeof GSCConfigSchema>;

// Default configurations template - ALL SENSITIVE VALUES MUST BE PROVIDED VIA CONFIG
export const DEFAULT_ANALYTICS_CONFIG: AnalyticsConfig = {
	gtm: {
		accountId: undefined, // MUST be provided via environment variable or config
		containerId: undefined, // MUST be provided via environment variable or config
		containerPublicId: undefined, // MUST be provided via environment variable or config
		workspaceId: undefined, // MUST be provided via environment variable or config
		googleAdsId: undefined, // MUST be provided via environment variable or config
		serviceAccount: {
			email: undefined, // MUST be provided via environment variable or config
			keyFile: undefined, // MUST be provided via environment variable or config
		},
		conversionLabels: {
			// All conversion labels must be provided via configuration
			form: undefined, // MUST be provided via environment variable or config
			whatsapp: undefined, // MUST be provided via environment variable or config
			purchase: undefined, // MUST be provided via environment variable or config
			add_to_cart: undefined, // MUST be provided via environment variable or config
		},
		environment: 'production',
		debug: false,
		retryAttempts: 3,
		timeout: 10000,
	},
	googleAds: {
		customerId: undefined, // MUST be provided via environment variable or config
		managerId: undefined, // MUST be provided via environment variable or config
		developerToken: undefined, // MUST be provided via environment variable or config
		serviceAccount: {
			email: undefined, // MUST be provided via environment variable or config
			keyFile: undefined, // MUST be provided via environment variable or config
		},
		conversionActions: {
			// All conversion actions must be provided via configuration
			whatsapp: undefined, // MUST be provided via environment variable or config
			form: undefined, // MUST be provided via environment variable or config
			purchase: undefined, // MUST be provided via environment variable or config
			add_to_cart: undefined, // MUST be provided via environment variable or config
		},
		apiVersion: 'v17',
		environment: 'production',
		debug: false,
		retryAttempts: 3,
		timeout: 10000,
	},
	postHog: {
		projectApiKey: undefined, // MUST be provided via environment variable or config
		host: 'https://us.i.posthog.com',
		personalApiKey: undefined, // Optional for CLI queries
		projectId: undefined, // Optional for API queries
		timeout: 5000,
		batchSize: 100,
		flushAt: 20,
		flushInterval: 10000,
		environment: 'production',
		debug: false,
		retryAttempts: 3,
	},
	meta: {
		pixelId: undefined, // MUST be provided via environment variable or config
		appId: undefined, // MUST be provided via environment variable or config
		accessToken: undefined, // Optional for server-side events
		testEventCode: undefined, // For development testing
		apiVersion: 'v21.0',
		environment: 'production',
		debug: false,
		retryAttempts: 3,
		timeout: 5000,
	},
	gsc: {
		siteUrl: undefined, // MUST be provided via environment variable or config
		serviceAccount: {
			email: undefined, // MUST be provided via environment variable or config
			keyFile: undefined, // MUST be provided via environment variable or config
		},
		environment: 'production',
		debug: false,
		retryAttempts: 3,
		timeout: 10000,
	},
};

/**
 * Load unified analytics configuration from environment variables
 * with fallback to defaults
 */
export function loadAnalyticsConfig(): AnalyticsConfig {
	const environment = getOptionalEnv('NODE_ENV', 'production') as
		| 'development'
		| 'staging'
		| 'production';
	const debug = getOptionalEnv('ANALYTICS_DEBUG', 'false') === 'true';

	return {
		gtm: {
			accountId: getOptionalEnv('GTM_ACCOUNT_ID', DEFAULT_ANALYTICS_CONFIG.gtm.accountId) || '',
			containerId:
				getOptionalEnv('GTM_CONTAINER_ID', DEFAULT_ANALYTICS_CONFIG.gtm.containerId) || '',
			containerPublicId:
				getOptionalEnv('GTM_CONTAINER_PUBLIC_ID', DEFAULT_ANALYTICS_CONFIG.gtm.containerPublicId) ||
				'',
			workspaceId:
				getOptionalEnv('GTM_WORKSPACE_ID', DEFAULT_ANALYTICS_CONFIG.gtm.workspaceId) || '',
			googleAdsId: getOptionalEnv('GOOGLE_ADS_ID', DEFAULT_ANALYTICS_CONFIG.gtm.googleAdsId) || '',
			serviceAccount: {
				email:
					getOptionalEnv(
						'GTM_SERVICE_ACCOUNT_EMAIL',
						DEFAULT_ANALYTICS_CONFIG.gtm.serviceAccount.email
					) || '',
				keyFile:
					getOptionalEnv(
						'GTM_SERVICE_ACCOUNT_KEY_FILE',
						DEFAULT_ANALYTICS_CONFIG.gtm.serviceAccount.keyFile
					) || '',
			},
			conversionLabels: {
				form:
					getOptionalEnv(
						'GTM_CONVERSION_LABEL_FORM',
						DEFAULT_ANALYTICS_CONFIG.gtm.conversionLabels.form
					) || '',
				whatsapp:
					getOptionalEnv(
						'GTM_CONVERSION_LABEL_WHATSAPP',
						DEFAULT_ANALYTICS_CONFIG.gtm.conversionLabels.whatsapp
					) || '',
				purchase: getOptionalEnv('GTM_CONVERSION_LABEL_PURCHASE'),
				add_to_cart: getOptionalEnv('GTM_CONVERSION_LABEL_ADD_TO_CART'),
			},
			environment,
			debug,
			retryAttempts: Number(getOptionalEnv('GTM_RETRY_ATTEMPTS', '3')),
			timeout: Number(getOptionalEnv('GTM_TIMEOUT', '10000')),
		},
		googleAds: {
			customerId:
				getOptionalEnv('GOOGLE_ADS_CUSTOMER_ID', DEFAULT_ANALYTICS_CONFIG.googleAds.customerId) ||
				DEFAULT_ANALYTICS_CONFIG.googleAds.customerId,
			managerId:
				getOptionalEnv('GOOGLE_ADS_MANAGER_ID', DEFAULT_ANALYTICS_CONFIG.googleAds.managerId) ||
				DEFAULT_ANALYTICS_CONFIG.googleAds.managerId,
			developerToken: getRequiredEnv('GOOGLE_ADS_DEVELOPER_TOKEN'),
			loginCustomerId: getOptionalEnv('GOOGLE_ADS_LOGIN_CUSTOMER_ID'),
			serviceAccount: {
				email:
					getOptionalEnv(
						'GOOGLE_ADS_SERVICE_ACCOUNT_EMAIL',
						DEFAULT_ANALYTICS_CONFIG.googleAds.serviceAccount.email
					) || DEFAULT_ANALYTICS_CONFIG.googleAds.serviceAccount.email,
				keyFile:
					getOptionalEnv(
						'GOOGLE_ADS_SERVICE_ACCOUNT_KEY_FILE',
						DEFAULT_ANALYTICS_CONFIG.googleAds.serviceAccount.keyFile
					) || DEFAULT_ANALYTICS_CONFIG.googleAds.serviceAccount.keyFile,
			},
			conversionActions: {
				whatsapp:
					getOptionalEnv(
						'GOOGLE_ADS_CONVERSION_ACTION_WHATSAPP',
						DEFAULT_ANALYTICS_CONFIG.googleAds.conversionActions.whatsapp
					) || DEFAULT_ANALYTICS_CONFIG.googleAds.conversionActions.whatsapp,
				form:
					getOptionalEnv(
						'GOOGLE_ADS_CONVERSION_ACTION_FORM',
						DEFAULT_ANALYTICS_CONFIG.googleAds.conversionActions.form
					) || DEFAULT_ANALYTICS_CONFIG.googleAds.conversionActions.form,
				purchase: getOptionalEnv('GOOGLE_ADS_CONVERSION_ACTION_PURCHASE'),
				add_to_cart: getOptionalEnv('GOOGLE_ADS_CONVERSION_ACTION_ADD_TO_CART'),
			},
			apiVersion:
				getOptionalEnv('GOOGLE_ADS_API_VERSION', DEFAULT_ANALYTICS_CONFIG.googleAds.apiVersion) ||
				DEFAULT_ANALYTICS_CONFIG.googleAds.apiVersion,
			environment,
			debug,
			retryAttempts: Number(getOptionalEnv('GOOGLE_ADS_RETRY_ATTEMPTS', '3')),
			timeout: Number(getOptionalEnv('GOOGLE_ADS_TIMEOUT', '10000')),
		},
		postHog: {
			projectApiKey: getRequiredEnv('POSTHOG_API_KEY'),
			host:
				getOptionalEnv('POSTHOG_HOST', DEFAULT_ANALYTICS_CONFIG.postHog.host) ||
				DEFAULT_ANALYTICS_CONFIG.postHog.host,
			personalApiKey: getOptionalEnv('POSTHOG_PERSONAL_API_KEY'),
			projectId: getOptionalEnv('POSTHOG_PROJECT_ID')
				? Number(getOptionalEnv('POSTHOG_PROJECT_ID')!)
				: undefined,
			timeout: Number(getOptionalEnv('POSTHOG_TIMEOUT', '5000')),
			batchSize: Number(getOptionalEnv('POSTHOG_BATCH_SIZE', '100')),
			flushAt: Number(getOptionalEnv('POSTHOG_FLUSH_AT', '20')),
			flushInterval: Number(getOptionalEnv('POSTHOG_FLUSH_INTERVAL', '10000')),
			environment,
			debug,
			retryAttempts: Number(getOptionalEnv('POSTHOG_RETRY_ATTEMPTS', '3')),
		},
		meta: {
			pixelId:
				getOptionalEnv('FACEBOOK_PIXEL_ID', DEFAULT_ANALYTICS_CONFIG.meta.pixelId) ||
				DEFAULT_ANALYTICS_CONFIG.meta.pixelId,
			appId:
				getOptionalEnv('FACEBOOK_APP_ID', DEFAULT_ANALYTICS_CONFIG.meta.appId) ||
				DEFAULT_ANALYTICS_CONFIG.meta.appId,
			accessToken: getOptionalEnv('FACEBOOK_ACCESS_TOKEN'),
			testEventCode: getOptionalEnv('FACEBOOK_TEST_EVENT_CODE'),
			apiVersion:
				getOptionalEnv('FACEBOOK_API_VERSION', DEFAULT_ANALYTICS_CONFIG.meta.apiVersion) ||
				DEFAULT_ANALYTICS_CONFIG.meta.apiVersion,
			environment,
			debug,
			retryAttempts: Number(getOptionalEnv('FACEBOOK_RETRY_ATTEMPTS', '3')),
			timeout: Number(getOptionalEnv('FACEBOOK_TIMEOUT', '5000')),
		},
		gsc: {
			siteUrl:
				getOptionalEnv('GSC_SITE_URL', DEFAULT_ANALYTICS_CONFIG.gsc.siteUrl) ||
				DEFAULT_ANALYTICS_CONFIG.gsc.siteUrl,
			serviceAccount: {
				email:
					getOptionalEnv(
						'GSC_SERVICE_ACCOUNT_EMAIL',
						DEFAULT_ANALYTICS_CONFIG.gsc.serviceAccount.email
					) || DEFAULT_ANALYTICS_CONFIG.gsc.serviceAccount.email,
				keyFile:
					getOptionalEnv(
						'GSC_SERVICE_ACCOUNT_KEY_FILE',
						DEFAULT_ANALYTICS_CONFIG.gsc.serviceAccount.keyFile
					) || DEFAULT_ANALYTICS_CONFIG.gsc.serviceAccount.keyFile,
			},
			environment,
			debug,
			retryAttempts: Number(getOptionalEnv('GSC_RETRY_ATTEMPTS', '3')),
			timeout: Number(getOptionalEnv('GSC_TIMEOUT', '10000')),
		},
	};
}

// ECOMMERCE_EVENTS imported from ../events.js - single source of truth

export const ALL_EVENTS = {
	...LEAD_GENERATION_EVENTS,
	...ECOMMERCE_EVENTS,
} as const;

// Re-export for backward compatibility and external usage
export { LEAD_GENERATION_EVENTS, ECOMMERCE_EVENTS, LEAD_SCORES };

/**
 * Platform-specific event mappings
 */
export const PLATFORM_MAPPINGS = {
	GTM: {
		LEAD_GENERATION: 'google_ads_conversion',
		ECOMMERCE: 'enhanced_ecommerce',
	},
	GOOGLE_ADS: {
		LEAD_GENERATION: 'conversion',
		ECOMMERCE: 'conversion',
	},
	POSTHOG: {
		LEAD_GENERATION: 'lead_event',
		ECOMMERCE: 'ecommerce_event',
	},
	META: {
		LEAD_GENERATION: 'custom',
		ECOMMERCE: 'track',
	},
} as const;
